import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import attachmentButton from '@/assets/icons/attachmentButton.svg';
import replyButton from '@/assets/icons/replyButton.svg';
import {
  PromptInput,
  PromptInputBody,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
  PromptInputToolbar,
  PromptInputTools,
  usePromptInputAttachments,
} from '@/components/ui/ai-elements/prompt-input';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ANALYTICS_EVENTS } from '@/config/analytics/events';
import { usePostHog } from '@/config/analytics/posthog';
import { transcribeAudio } from '@/features/assistant/hooks/transcription';
import { usePromptEnhancement } from '@/features/assistant/hooks/usePromptEnhancement';
import { useVoiceInput } from '@/features/assistant/hooks/useVoiceInput';
import { ACCEPTED_FILE_TYPES, CHAT_STATUS, type ChatStatus } from '@/utils/constants';

import { VoiceInputActionButton } from './VoiceInputActionButton';

interface PromptProps {
  disabled?: boolean;
  initialValue?: string;
  isStreaming?: boolean;
  onAttachmentClick?: () => void;
  onFilesAdded?: (files: Array<File>) => void;
  onSend?: (value: string) => void;
  onStop?: () => void;
  placeholder?: string;
  status?: ChatStatus;
}

function PromptContent({
  disabled,
  isEnhancing,
  isStreaming,
  onAttachmentClick,
  onEnhance,
  onFilesAdded,
  onStop,
  placeholder,
  setText,
  showEnhancementHint,
  showUndoHint,
  status,
  text,
  textareaRef,
}: {
  disabled?: boolean;
  isEnhancing?: boolean;
  isStreaming?: boolean;
  onAttachmentClick?: () => void;
  onEnhance?: () => void;
  onFilesAdded?: (files: Array<File>) => void;
  onStop?: () => void;
  placeholder: string;
  setText: React.Dispatch<React.SetStateAction<string>>;
  showEnhancementHint?: boolean;
  showUndoHint?: boolean;
  status: ChatStatus;
  text: string;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const attachments = usePromptInputAttachments();
  const posthog = usePostHog();

  useEffect(() => {
    const fileInput = attachments.fileInputRef.current;
    if (!fileInput || !onFilesAdded) {
      return;
    }

    const handleFileChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (!target.files?.length) {
        return;
      }

      // Filter to only accept allowed file types
      const newFiles = Array.from(target.files).filter((file) => {
        const fileName = file.name.toLowerCase();
        return ACCEPTED_FILE_TYPES.some((ext) => fileName.endsWith(ext.toLowerCase()));
      });

      if (newFiles.length > 0) {
        onFilesAdded(newFiles);
      }
    };

    fileInput.addEventListener('change', handleFileChange, true);
    return () => fileInput.removeEventListener('change', handleFileChange, true);
  }, [attachments.fileInputRef, onFilesAdded]);

  const handleAddAttachments = useCallback(() => {
    attachments.openFileDialog();
    onAttachmentClick?.();
  }, [attachments, onAttachmentClick]);

  const {
    cancelRecording,
    error: voiceError,
    isListening,
    isSupported,
    permission,
    startRecording,
    stopRecording,
  } = useVoiceInput();

  const { i18n, t } = useTranslation();
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);

  const handleVoiceButtonClick = useCallback(async () => {
    if (isListening) {
      return;
    }
    await startRecording();
    posthog.capture(ANALYTICS_EVENTS.ASSISTANT_VOICE_TO_TEXT_ACTIVATED, {});
  }, [isListening, startRecording, posthog]);

  const handleConfirmRecording = useCallback(async () => {
    try {
      setIsProcessingVoice(true);
      const audioBlob = await stopRecording();

      if (audioBlob) {
        // Convert Blob to File
        const file = new File([audioBlob], 'voice_input.mp3', {
          type: 'audio/mp3',
        });

        const transcriptionLanguage = i18n.resolvedLanguage || i18n.language || 'en';
        const response = await transcribeAudio(file, transcriptionLanguage);
        if (response?.transcript) {
          setText((prev) => (prev ? `${prev} ${response.transcript}` : response.transcript));
        }
      }
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error(t('assistant.prompt.transcribeFailed'));
    } finally {
      setIsProcessingVoice(false);
    }
  }, [i18n.language, i18n.resolvedLanguage, setText, stopRecording, t]);

  const handleCancelRecording = () => {
    cancelRecording();
    // Assuming setIsRecording refers to the processing state or a local recording state.
    // If it refers to `isListening` from `useVoiceInput`, that's managed internally.
    // If it refers to `isProcessingVoice`, then:
    setIsProcessingVoice(false);
  };

  const canSend = text.trim() && !disabled && status === CHAT_STATUS.READY && !isEnhancing;

  return (
    <>
      <PromptInputToolbar className="h-6 w-full min-w-0 flex-shrink-0 justify-between p-0">
        <PromptInputTools className="!p-0">
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                aria-label="Attach file"
                className="hover:bg-elevated-subtle flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors"
                onClick={handleAddAttachments}
              >
                <img alt="Attach" className="icon-themed h-4 w-4" src={attachmentButton} />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('assistant.prompt.uploadFiles')}</p>
            </TooltipContent>
          </Tooltip>
        </PromptInputTools>

        <div className="flex items-center gap-4">
          <VoiceInputActionButton
            disabled={disabled ?? status !== CHAT_STATUS.READY}
            error={voiceError}
            handleCancelClick={handleCancelRecording}
            handleConfirmClick={handleConfirmRecording}
            handleVoiceButtonClick={handleVoiceButtonClick}
            hasPermission={permission}
            isListening={isListening}
            isProcessing={isProcessingVoice}
            isSupported={isSupported}
          />
          {isStreaming ? (
            <Button
              aria-label="Stop generation"
              className="bg-destructive hover:bg-destructive/90 flex h-6 w-6 items-center justify-center rounded-full border-0 p-0"
              onClick={onStop}
              type="button"
            >
              <div className="bg-destructive-foreground h-2 w-2 rounded-sm" />
            </Button>
          ) : (
            <PromptInputSubmit
              aria-label="Send message"
              className="bg-elevated-hover h-6 w-6 rounded-full border-0 p-0 hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={!canSend}
              status={status}
            >
              <img alt="Send" className="icon-themed h-6 w-6" src={replyButton} />
            </PromptInputSubmit>
          )}
        </div>
      </PromptInputToolbar>

      <PromptInputBody className="flex min-w-0 flex-1 items-end !bg-transparent pt-1 pb-2">
        <PromptInputTextarea
          className="placeholder:text-neutral-mid max-h-[60px] min-h-[20px] w-full min-w-0 resize-none !bg-transparent px-0 py-0 text-sm leading-5"
          disabled={(disabled ?? status !== CHAT_STATUS.READY) || isEnhancing}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Tab' && showEnhancementHint) {
              e.preventDefault();
              onEnhance?.();
            }
          }}
          placeholder={placeholder}
          ref={textareaRef}
          value={text}
        />
      </PromptInputBody>

      {(showEnhancementHint || isEnhancing || showUndoHint) && (
        <div className="text-neutral-mid px-0 pb-1 text-xs">
          {isEnhancing ? (
            <span className="animate-pulse">{t('assistant.promptEnhancement.enhancing')}</span>
          ) : showUndoHint ? (
            <span>{t('assistant.promptEnhancement.undoHint')}</span>
          ) : (
            <span>
              <kbd className="border-border-subtle bg-back-bg2 rounded border px-1 font-sans">
                Tab
              </kbd>{' '}
              {t('assistant.promptEnhancement.tabHint')}
            </span>
          )}
        </div>
      )}
    </>
  );
}

export function Prompt({
  disabled,
  initialValue = '',
  isStreaming = false,
  onAttachmentClick,
  onFilesAdded,
  onSend,
  onStop,
  placeholder: placeholderProp,
  status = CHAT_STATUS.READY,
}: PromptProps) {
  const { t } = useTranslation();
  const placeholder = placeholderProp ?? t('assistant.prompt.placeholder');
  const [text, setText] = useState(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { handleEnhance, isEnhancing, resetAfterSubmit, showEnhancementHint, showUndoHint } =
    usePromptEnhancement(text, setText, textareaRef);

  const handleSubmit = useCallback(
    (message: PromptInputMessage, event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (status !== CHAT_STATUS.READY || disabled) {
        return;
      }

      const trimmed = message.text?.trim() ?? '';
      if (!trimmed && (!message.files || message.files.length === 0)) {
        return;
      }

      onSend?.(trimmed);
      setText('');
      resetAfterSubmit();
    },
    [onSend, status, disabled, resetAfterSubmit],
  );

  const handleError = useCallback((err: { code: string; message: string }) => {
    console.error('File upload error:', err.message);
  }, []);

  return (
    <div className="bg-card relative flex min-h-[92px] w-full flex-col gap-2 overflow-hidden rounded-t-2xl p-3 shadow-sm">
      <div className="relative z-10 flex min-h-0 w-full flex-1 flex-col-reverse overflow-hidden">
        <PromptInput
          accept={ACCEPTED_FILE_TYPES}
          className="flex w-full min-w-0 flex-1 flex-col-reverse !rounded-none border-0 !bg-transparent !p-0 shadow-none"
          multiple
          onError={handleError}
          onSubmit={handleSubmit}
        >
          <PromptContent
            isEnhancing={isEnhancing}
            isStreaming={isStreaming}
            onEnhance={handleEnhance}
            placeholder={placeholder}
            setText={setText}
            showEnhancementHint={showEnhancementHint}
            showUndoHint={showUndoHint}
            text={text}
            textareaRef={textareaRef}
            {...(disabled !== undefined && { disabled })}
            status={status}
            {...(onAttachmentClick !== undefined && {
              onAttachmentClick,
            })}
            {...(onFilesAdded !== undefined && {
              onFilesAdded,
            })}
            {...(onStop !== undefined && { onStop })}
          />
        </PromptInput>
      </div>
    </div>
  );
}
